import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, TOKEN_KEY } from '../api/client';

const USER_KEY = 'polyrion_auth_user';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [bootstrapping, setBootstrapping] = useState(() => Boolean(localStorage.getItem(TOKEN_KEY)));

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const persistSession = useCallback((payload) => {
    localStorage.setItem(TOKEN_KEY, payload.token);
    localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
    setUser(payload.user);
  }, []);

  const register = useCallback(
    async (name, email, password, passwordConfirmation) => {
      const { data } = await api.post('/v1/auth/user/register', {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
        device_name: 'polyrion-web',
      });
      persistSession(data);
      return data.user;
    },
    [persistSession],
  );

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post('/v1/auth/user/login', {
        email,
        password,
        device_name: 'polyrion-web',
      });
      persistSession(data);
      return data.user;
    },
    [persistSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/v1/auth/logout');
    } catch {
      // ignore network errors; still clear local session
    }
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBootstrapping(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/v1/auth/me');
        if (!cancelled) {
          localStorage.setItem(USER_KEY, JSON.stringify(data));
          setUser(data);
        }
      } catch {
        if (!cancelled) {
          clearSession();
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      bootstrapping,
      login,
      register,
      logout,
    }),
    [user, bootstrapping, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
